/**
 * The source PDF's pages as images, rendered once and kept for the session.
 * A job page warms this as soon as it knows the PDF, so opening a value's
 * source is instant rather than a download, a parse and a render.
 */

type PdfDocument = import('pdfjs-dist/legacy/build/pdf.mjs').PDFDocumentProxy;

export interface PageImage {
  src: string;
  width: number;
  height: number;
}

const RENDER_WIDTH = 2000;
const documents = new Map<string, Promise<PdfDocument>>();
const pages = new Map<string, Promise<PageImage>>();

function loadDocument(url: string): Promise<PdfDocument> {
  let pending = documents.get(url);
  if (!pending) {
    pending = (async () => {
      const [pdfjs, worker] = await Promise.all([
        import('pdfjs-dist/legacy/build/pdf.mjs'),
        import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url'),
      ]);
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
      return pdfjs.getDocument({ url, disableRange: true, disableStream: true }).promise;
    })();
    pending.catch(() => documents.delete(url));
    documents.set(url, pending);
  }
  return pending;
}

async function render(url: string, index: number): Promise<PageImage> {
  const pdf = await loadDocument(url);
  const page = await pdf.getPage(index + 1);
  const base = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({ scale: RENDER_WIDTH / base.width });
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  await page.render({ canvas, viewport }).promise;
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.88),
  );
  if (!blob) {
    throw new Error('The page could not be drawn.');
  }
  return { src: URL.createObjectURL(blob), width: canvas.width, height: canvas.height };
}

/** One page (0-based) as an image; rendered at most once per URL. */
export function pageImage(url: string, index: number): Promise<PageImage> {
  const key = `${url}#${index}`;
  let pending = pages.get(key);
  if (!pending) {
    pending = render(url, index);
    pending.catch(() => pages.delete(key));
    pages.set(key, pending);
  }
  return pending;
}

/** Render these pages in order, in the background; failures are left for the viewer to report. */
export async function warmPages(url: string, indexes: number[]): Promise<void> {
  for (const index of indexes) {
    await pageImage(url, index).catch(() => undefined);
  }
}
